'use client'

import { compact, filter, includes, map, uniqBy } from 'lodash'
import clsx from 'clsx'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useMemo, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { RadioGroup } from '@headlessui/react'

import { AtHomeAddressPicker, type AtHomeAddressResult } from '@/features/booking/at-home-address'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'
import { isOutsideServiceArea } from '../../lib/serviceArea'
import { useAddSavedAddressMutation, useDeleteSavedAddressMutation, useGetMyInfosQuery } from '@/core/graphql/hooks'

import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { useSchedules } from '../../model/useSchedules'
import { BookingStep } from './BookingStep'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'
import { useGetMeetingLink } from '@/shared/api/meeting-link'

dayjs.extend(isBetween)

function getBookingMeetingPlaceLabel(meetingPlace: AvailabilityMeetingPlace) {
  switch (meetingPlace) {
    case AvailabilityMeetingPlace.Center:
      return t`At clinic`
    case AvailabilityMeetingPlace.AtHome:
      return t`At home`
    case AvailabilityMeetingPlace.VisioConference:
      return t`Video visit`
    case AvailabilityMeetingPlace.SecondOpinion:
      return t`Second Opinion`
  }
}

export function MeetingPlaceStep() {
  const { goToStep, isStepCompleted, state } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.MEETING_PLACE)

  if (state.currentStep === BookingStepEnum.MEETING_PLACE) {
    return (
      <BookingStep className="sm:!rounded-br-none">
        <BookingStep.Title>
          <Trans>Meeting place</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <Addresses meetingLink={state.meetingLinkSlug} />
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return (
      <Summary
        icon="📍"
        label={t`Meeting place`}
        onEdit={() => goToStep(BookingStepEnum.MEETING_PLACE)}
        text={state.meetingPlaceDescription ?? state.meetingPlace ?? ''}
      />
    )
  }

  return (
    <IncompletedStepLabel icon="📍">
      <Trans>Meeting place</Trans>
    </IncompletedStepLabel>
  )
}

type AddressesProps = {
  meetingLink: string
}

function Addresses({ meetingLink: meetingLinkSlug }: AddressesProps) {
  const { state, dispatch, goToNextStep } = useBooking()

  // Get speciality from context
  const speciality = state.selectedSpeciality

  // No place is pre-selected: the address UI (search / manual entry / establishment list) only
  // appears once the patient explicitly picks a place.
  const [selectedMeetingPlace, setSelectedMeetingPlace] = useState<AvailabilityMeetingPlace | null>(null)

  const { meetingLink, isLoadingMeetingLink } = useGetMeetingLink(meetingLinkSlug)
  const schedules = useSchedules(meetingLink)

  const secondOpinionAvailabilities = useMemo(() => {
    return meetingLink?.availabilities.filter((availability) => availability.meetingPlace === AvailabilityMeetingPlace.SecondOpinion)
  }, [meetingLink])

  const establishmentAddressOptions = useMemo(() => {
    const addressOptions = []
    const addresses = map(
      filter(schedules, (schedule) => includes(schedule.specialities, speciality)),
      'address'
    )
    for (const address of addresses) {
      if (address !== null && address !== undefined) addressOptions.push({ label: address.description, value: address.placeId })
    }
    const uniqueOptions = uniqBy(addressOptions, 'value')

    // Prioritize by postal code if available
    if (state.patientLocation?.postalCode) {
      return uniqueOptions.sort((a, b) => {
        const aHasPostalCode = a.label.includes(state.patientLocation!.postalCode!)
        const bHasPostalCode = b.label.includes(state.patientLocation!.postalCode!)
        if (aHasPostalCode && !bHasPostalCode) return -1
        if (!aHasPostalCode && bHasPostalCode) return 1
        return 0
      })
    }

    return uniqueOptions
  }, [schedules, speciality, state.patientLocation])

  const availabilityMeetingPlaceOptions = useMemo(
    () =>
      compact([
        establishmentAddressOptions.length
          ? { label: getBookingMeetingPlaceLabel(AvailabilityMeetingPlace.Center), value: AvailabilityMeetingPlace.Center }
          : null,
        { label: getBookingMeetingPlaceLabel(AvailabilityMeetingPlace.AtHome), value: AvailabilityMeetingPlace.AtHome },
        { label: getBookingMeetingPlaceLabel(AvailabilityMeetingPlace.VisioConference), value: AvailabilityMeetingPlace.VisioConference },
        !!secondOpinionAvailabilities?.length && {
          label: getBookingMeetingPlaceLabel(AvailabilityMeetingPlace.SecondOpinion),
          value: AvailabilityMeetingPlace.SecondOpinion,
        },
      ]),
    [establishmentAddressOptions, secondOpinionAvailabilities]
  )

  // Saved addresses hooks
  const { data: myInfosData } = useGetMyInfosQuery({ errorPolicy: 'ignore' })
  const myInfos = myInfosData?.myInfos
  const [addSavedAddress] = useAddSavedAddressMutation({ refetchQueries: ['getMyInfos'] })
  const [deleteSavedAddress] = useDeleteSavedAddressMutation({ refetchQueries: ['getMyInfos'] })
  const [serviceAreaWarning, setServiceAreaWarning] = useState(false)

  // Get AT_HOME availability's service area config
  const atHomeServiceArea = useMemo(() => {
    const atHomeAvailability = meetingLink?.availabilities.find((a: any) => a.meetingPlace === AvailabilityMeetingPlace.AtHome)
    return atHomeAvailability?.serviceArea || null
  }, [meetingLink])

  const handleAtHomeSelect = (result: AtHomeAddressResult) => {
    if (atHomeServiceArea) {
      setServiceAreaWarning(isOutsideServiceArea(atHomeServiceArea, result.postalCode, result.coordinates))
    }
    dispatch({
      type: 'SET_MEETING_PLACE_DETAILS',
      payload: {
        apartment: result.apartment,
        floor: result.floor,
        doorCode: result.doorCode,
        instructions: result.instructions,
        postalCode: result.postalCode,
        coordinates: result.coordinates,
      },
    })
    handleSelectMeetingPlace(AvailabilityMeetingPlace.AtHome, result.placeId || null, result.description)
  }

  const handleSaveAddress = async (address: any) => {
    try {
      await addSavedAddress({ variables: { payload: address } })
    } catch (e) {
      console.error('Error saving address:', e)
    }
  }

  const handleDeleteAddress = async (addressId: string) => {
    try {
      await deleteSavedAddress({ variables: { addressId } })
    } catch (e) {
      console.error('Error deleting address:', e)
    }
  }

  const handleSelectMeetingPlace = (meetingPlace: AvailabilityMeetingPlace, meetingPlaceId: string | null, description: string) => {
    // Store meeting place information in context
    console.log('🔍 [ADDRESSES] Meeting place:', meetingPlace)
    if (meetingPlace === AvailabilityMeetingPlace.SecondOpinion) {
      const selectedAvailability = meetingLink?.availabilities.find((availability) => availability.meetingPlace === meetingPlace)
      dispatch({ type: 'SET_HAS_FORM_TEMPLATE', payload: !!selectedAvailability?.formTemplate })
    }
    dispatch({
      type: 'SET_MEETING_PLACE',
      payload: {
        place: meetingPlace,
        placeId: meetingPlaceId,
        description: description,
      },
      goToNextStep: true,
    })
  }

  if (isLoadingMeetingLink) return <MeetingPlaceSkeleton />

  return (
    <div className="flex flex-col space-y-4">
      <RadioGroup value={selectedMeetingPlace} onChange={setSelectedMeetingPlace}>
        <RadioGroup.Label className="sr-only">
          <Trans>Meeting place</Trans>
        </RadioGroup.Label>
        <div className="relative -space-y-px rounded-md bg-white">
          {availabilityMeetingPlaceOptions.map((option, optionIdx) => (
            <RadioGroup.Option
              key={option.value}
              value={option.value}
              data-testid={`place-${option.value}`}
              className={({ checked }) =>
                clsx(
                  {
                    'rounded-tl-md rounded-tr-md': optionIdx === 0,
                    'rounded-bl-md rounded-br-md': optionIdx === availabilityMeetingPlaceOptions.length - 1,
                    'z-10 border-indigo-200 bg-indigo-50': checked,
                    'border-gray-200': !checked,
                  },
                  'relative flex cursor-pointer flex-col border border-solid p-4 focus:outline-none md:grid md:grid-cols-3 md:pl-4 md:pr-6'
                )
              }
              onClick={() => {
                if (option.value === AvailabilityMeetingPlace.VisioConference || option.value === AvailabilityMeetingPlace.SecondOpinion) {
                  handleSelectMeetingPlace(option.value, null, option.label)
                }
              }}
            >
              <RadioGroup.Label as="span" className="text-sm text-gray-900 font-medium">
                {option.label}
              </RadioGroup.Label>
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
      {selectedMeetingPlace === AvailabilityMeetingPlace.AtHome && (
        <>
          <AtHomeAddressPicker
            savedAddresses={myInfos?.savedAddresses || []}
            profileAddress={
              myInfos?.contactInfo
                ? {
                    street: myInfos.contactInfo.street,
                    city: myInfos.contactInfo.city,
                    cp: myInfos.contactInfo.cp,
                    country: myInfos.contactInfo.country,
                  }
                : null
            }
            isLoggedIn={state.isLoggedIn}
            onSelect={handleAtHomeSelect}
            onSaveAddress={state.isLoggedIn ? handleSaveAddress : undefined}
            onDeleteAddress={state.isLoggedIn ? handleDeleteAddress : undefined}
          />
          {serviceAreaWarning && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-800">
                <Trans>Your address is outside the service area</Trans>
              </p>
            </div>
          )}
        </>
      )}

      {selectedMeetingPlace === AvailabilityMeetingPlace.Center && (
        <div>
          <p className="text-sm font-medium leading-6 text-gray-900">
            <Trans>Address</Trans>
          </p>
          <ul role="list" className="bg-white border border-gray-200 divide-y divide-gray-200 overflow-hidden rounded-md">
            {establishmentAddressOptions.map((addressOption) => (
              <li key={addressOption.value}>
                <div
                  className="block cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    handleSelectMeetingPlace(selectedMeetingPlace, addressOption.value, addressOption.label)
                  }}
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="truncate text-sm font-medium text-indigo-600">{addressOption.label}</p>
                      {state.patientLocation?.postalCode && addressOption.label.includes(state.patientLocation.postalCode) && (
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                          <Trans>Near you</Trans>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function MeetingPlaceSkeleton() {
  return (
    <div className="animate-pulse flex flex-col gap-2">
      <div className="h-12 rounded bg-slate-200" />
      <div className="h-12 rounded bg-slate-200" />
      <div className="h-12 rounded bg-slate-200" />
      <div className="h-12 rounded bg-slate-200" />
    </div>
  )
}
